import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { buySubscriptions, verifyPayment, activateFreeSubscription } from "../../store/slices/booking";
import { useLocation } from "react-router-dom";

const formatDuration = (duration, durationType) => {
  if (!duration) return null;
  const unit =
    durationType === "days"
      ? duration === 1
        ? "Day"
        : "Days"
      : duration === 1
        ? "Month"
        : "Months";
  return `${duration} ${unit}`;
};

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

const Checkout = () => {
  const location = useLocation();
  const [plan, setPlan] = useState(location.state?.plan || null);

  console.log(plan);

  const [isPaying, setIsPaying] = useState(false);
  const rzpOpenedRef = useRef(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  useEffect(() => {
    if (!location.state?.plan) {
      navigate("/user/membership");
    }
  }, [location, navigate]);

  /* -- >  Razorpay loader --- > */
  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const openRazorpay = (orderData) => {
    if (!orderData || rzpOpenedRef.current) return;
    rzpOpenedRef.current = true;

    const options = {
      key: orderData.keyId,
      amount: orderData.amount,
      currency: orderData.currency || "INR",
      order_id: orderData.orderId,
      name: "KVL Matrimony",
      description: plan?.name || "Subscription Plan",
      prefill: {
        name: user?.name,
        email: user?.email,
        contact: user?.mobileNumber,
      },
      handler: async (response) => {
        try {
          await dispatch(
            verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            }),
          ).unwrap();
          toast.success("Payment successful! Your plan is now active.");
          setIsPaying(false);
          rzpOpenedRef.current = false;
          setTimeout(() => navigate("/user/home"), 1500);
        } catch (err) {
          toast.error(
            err?.message || "Payment verification failed. Contact support.",
          );
          setIsPaying(false);
          rzpOpenedRef.current = false;
        }
      },
      modal: {
        ondismiss: () => {
          toast.info("Payment cancelled.");
          setIsPaying(false);
          rzpOpenedRef.current = false;
        },
      },
      theme: { color: "#c0392b" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handleMakePayment = async () => {
    if (isPaying) return;

    try {
      setIsPaying(true);

      // ==========================================
      // FREE PLAN
      // ==========================================
      if (Number(plan?.price) === 0) {
        const response = await dispatch(
          activateFreeSubscription({
            planId: plan._id,
          })
        ).unwrap();

        toast.success(
          response?.message || "Free plan activated successfully!"
        );

        setIsPaying(false);

        setTimeout(() => {
          navigate("/user/home");
        }, 1500);

        return;
      }

      // ==========================================
      // PAID PLAN
      // ==========================================

      const isLoaded = await loadRazorpay();

      if (!isLoaded) {
        toast.error(
          "Payment gateway failed to load. Please try again."
        );
        setIsPaying(false);
        return;
      }

      const response = await dispatch(
        buySubscriptions({
          planId: plan._id,
        })
      ).unwrap();

      const orderData = response?.data ?? response;

      openRazorpay(orderData);
    } catch (err) {
      toast.error(
        err ||
        "Failed to process subscription. Please try again."
      );

      setIsPaying(false);
    }
  };

  if (!plan) {
    return (
      <div className="checkout-loading">
        <div className="spinner-border text-danger" />
        <p>Loading plan details...</p>
      </div>
    );
  }

  const base = plan?.price ?? 0;
  const total = base;
  const durationLabel = formatDuration(plan.duration, plan.durationType);

  const limitItems = plan.limits
    ? Object.entries(plan.limits).map(
      ([key, val]) => `${capitalize(key.replace(/([A-Z])/g, " $1"))}: ${val}`,
    )
    : [];

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />

      <div className="checkout-page">
        <Container>
          <Row className="justify-content-center">
            <Col md={6} lg={5}>
              <div className="checkout-header">
                <h4>Complete Your Subscription</h4>
              </div>

              <Card className="checkout-card">
                {isPaying && (
                  <div className="checkout-overlay">
                    <div className="spinner-border text-danger mb-2" />
                    <span>Processing payment...</span>
                  </div>
                )}

                {/* Plan name badge */}
                <div className="plan-badge">{capitalize(plan.name)}</div>


                {/* Duration */}
                {durationLabel && (
                  <div className="plan-duration">
                    <span>Duration</span>
                    <strong>{durationLabel}</strong>
                  </div>
                )}


                <hr className="checkout-divider" />

                {/* Pricing breakdown */}
                <div className="price-row">
                  <span>Base Price</span>
                  <span>
                    {base === 0
                      ? "FREE"
                      : `₹ ${base.toLocaleString("en-IN")}`}
                  </span>
                </div>

                <div className="price-row total">
                  <span>Total Payable</span>
                  <span>
                    {total === 0
                      ? "FREE"
                      : `₹ ${total.toLocaleString("en-IN")}`}
                  </span>
                </div>
                <Button
                  className="pay-button"
                  disabled={isPaying}
                  onClick={handleMakePayment}
                >
                  {isPaying
                    ? "Processing..."
                    : Number(plan.price) === 0
                      ? "Activate Free Plan"
                      : `Pay ₹ ${total.toLocaleString("en-IN")}`}
                </Button>
                <p className="secure-note">
                  {Number(plan.price) === 0
                    ? "✓ No payment required"
                    : "🔒 100% secure payment via Razorpay"}
                </p>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Checkout;
